import { FetchHttpClient, HttpClient } from 'effect/unstable/http'
import { Effect, Semaphore } from 'effect'
import type { DocumentStructure, PageStructuredText } from '../../models'
import {
  MuPDFPlatform,
  type MuPDFPageRenderError,
  type MuPDFPageTextExtractionError,
  type MuPDFWorkerCrashedError
} from './mupdf-platform'

export type MuPDFRenderingEngine = {
  documentStructure: DocumentStructure
  renderPage: (
    pageNumber: number
  ) => Effect.Effect<ImageBitmap, MuPDFPageRenderError | MuPDFWorkerCrashedError>
  getPageStructuredText: (
    pageNumber: number
  ) => Effect.Effect<PageStructuredText, MuPDFPageTextExtractionError | MuPDFWorkerCrashedError>
}

const TARGET_DPI = 96 * 2
const RENDER_CONCURRENCY = 2
const MAX_RENDER_PIXELS = 2_000_000

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function loadDocumentBytes(httpClient: HttpClient.HttpClient, documentUrl: string) {
  return Effect.gen(function* () {
    const response = yield* httpClient.get(documentUrl).pipe(
      Effect.provideService(FetchHttpClient.RequestInit, {
        credentials: 'omit'
      })
    )
    if (response.status < 200 || response.status >= 300) {
      return yield* Effect.fail(new Error(`Failed to fetch PDF document: HTTP ${response.status}`))
    }

    return yield* response.arrayBuffer
  }).pipe(Effect.mapError(toError))
}

/**
 * Captures the physical services once while constructing the renderer Layer.
 * Every opened engine then contains only owned Effect logic and a scoped
 * MuPDF document session; no caller can inject a Promise or raw Worker.
 */
export const makeMuPDFRenderingEngine = Effect.gen(function* () {
  const httpClient = (yield* HttpClient.HttpClient).pipe(
    HttpClient.transformResponse(Effect.provideService(HttpClient.TracerPropagationEnabled, false))
  )
  const mupdfPlatform = yield* MuPDFPlatform

  return ({ documentUrl, devicePixelRatio }: { documentUrl: string; devicePixelRatio: number }) =>
    Effect.gen(function* () {
      const documentBytes = yield* loadDocumentBytes(httpClient, documentUrl)
      const document = yield* mupdfPlatform.openDocument(documentBytes)
      const semaphore = yield* Semaphore.make(RENDER_CONCURRENCY)

      const renderPage = (pageNumber: number) =>
        Effect.gen(function* () {
          const bounds = document.documentStructure.pages[pageNumber - 1]?.bounds
          if (!bounds) {
            return yield* Effect.die(new Error(`Missing bounds for page ${pageNumber}`))
          }

          const scale = calculateRenderScale(devicePixelRatio, bounds)
          return yield* document.renderPageAsImage(pageNumber, scale)
        }).pipe(semaphore.withPermits(1))

      const getPageStructuredText = (pageNumber: number) =>
        Effect.map(document.extractPageText(pageNumber), (textRects) => ({
          pageNumber,
          textRects
        })).pipe(semaphore.withPermits(1))

      return {
        documentStructure: document.documentStructure,
        renderPage,
        getPageStructuredText
      } satisfies MuPDFRenderingEngine
    })
})

/**
 * Compute the MuPDF rasterization scale for a page.
 *
 * This controls the **bitmap size** produced by MuPDF (`toPixmap`). Since bitmap memory grows roughly
 * with the number of pixels (~ \(scale^2\)), we clamp scale by a pixel budget to avoid iOS Safari
 * tab kills on high-DPR devices / large pages.
 */
function calculateRenderScale(
  devicePixelRatio: number, // Physical device pixel ratio (e.g. 2/3 on retina)
  bounds: { width: number; height: number } // Page bounds in PDF points (pt)
): number {
  // Cap DPR contribution so retina devices don't blow up raster size.
  const cappedDpr = Math.min(devicePixelRatio, 2)
  // Convert target DPI into a PDF-pt-to-pixel scale (72pt == 1in).
  const baseScale = (cappedDpr * TARGET_DPI) / 72
  // Pixel-budget cap expressed as a max allowable scale for this page area.
  const maxScale = Math.sqrt(MAX_RENDER_PIXELS / (bounds.width * bounds.height))
  // Choose the smaller scale so we never exceed the pixel budget.
  return Math.min(baseScale, maxScale)
}
